/**
 * BrandOnePagerPage
 * 8.3 - A generic one-pager any host can edit + share with ALL the brands they
 * work with (and future ones) to show the value of their client experience.
 *
 * Modeled after the demo sales pages (MUASalesPage etc) but every visual asset
 * is host-overridable, so Shelsea (or any host) can swap in her own hero image,
 * tagline, intro, value bullets, social links, and product cards.
 *
 * Routes:
 *   /host/v2/:eventId/brand-onepager  (host edits, requires auth)
 *   /b/:providerId                    (public, no auth, for brands to view)
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Camera,
  Edit3,
  Save,
  ExternalLink,
  Plus,
  Sparkles,
  Instagram,
  Globe,
  ShoppingBag,
  X,
  Check,
  Upload,
  Eye,
  Share,
  ArrowLeft,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandOnePagerPage() {
  const { eventId, providerId: paramProviderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicView = location.pathname.startsWith("/b/");

  const [providerId, setProviderId] = useState(paramProviderId || "");
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let pid = paramProviderId;
        if (!pid && eventId) {
          // Resolve providerId from event
          const res = await fetch(`${API}/api/events/${eventId}/public`);
          if (res.ok) {
            const e = await res.json();
            pid = e.provider_profile_id || e.provider_id || "";
          }
        }
        if (!pid && !isPublicView) {
          // Fallback: load my profile
          const token = localStorage.getItem("session_token");
          const me = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (me.ok) {
            const u = await me.json();
            const prf = await fetch(`${API}/api/admin/database/provider_profiles?user_id=${u.user_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (prf.ok) {
              const p = await prf.json();
              pid = (p.documents?.[0] || {}).provider_id;
            }
          }
        }
        setProviderId(pid);
        if (!pid) { setNotFound(true); return; }

        const url = isPublicView
          ? `${API}/api/public/brand-onepager/${pid}`
          : `${API}/api/host/brand-onepager/${pid}`;
        const headers = isPublicView ? {} : { Authorization: `Bearer ${localStorage.getItem("session_token")}` };
        const res = await fetch(url, { headers });
        if (res.ok) {
          setData(await res.json());
        } else if (res.status === 404) {
          setNotFound(true);
        }
      } catch (_) { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [eventId, paramProviderId, isPublicView]);

  const save = async () => {
    if (!providerId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/host/brand-onepager/${providerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          display_name: data.display_name,
          tagline: data.tagline,
          hero_image_url: data.hero_image_url,
          intro: data.intro,
          value_bullets: data.value_bullets,
          social_links: data.social_links,
          cover_color: data.cover_color,
        }),
      });
      if (res.ok) {
        toast.success("One-pager saved");
        setEditing(false);
      } else toast.error("Save failed");
    } catch (_) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("folder", "brand-onepager");
    const res = await fetch(`${API}/api/pilot/upload-image`, { method: "POST", body: fd });
    if (res.ok) {
      const j = await res.json();
      setData({ ...data, hero_image_url: j.url });
      toast.success("Hero uploaded");
    } else toast.error("Upload failed");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="brand-onepager-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...</div>;
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 text-center" data-testid="brand-onepager-empty">
        <Sparkles className="w-10 h-10 text-gray-300 mb-3" />
        <h1 className="text-xl font-semibold text-gray-800">No brand one-pager yet</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-sm">
          {isPublicView
            ? "This host has not published a brand one-pager. Check back soon."
            : "Build a one-pager from your host dashboard to share with brands and clients."}
        </p>
      </div>
    );
  }

  const cover = data.cover_color || "#9c57f5";
  const products = data.products || [];

  return (
    <div className="min-h-screen bg-white" data-testid="brand-onepager-page">
      {/* Top bar */}
      {!isPublicView && (
        <div className="border-b bg-white sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="onepager-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => window.open(`/b/${providerId}`, "_blank")} data-testid="onepager-preview-public">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview public
              </Button>
              {editing ? (
                <Button size="sm" onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="onepager-save">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving..." : "Save"}
                </Button>
              ) : (
                <Button size="sm" onClick={() => setEditing(true)} variant="outline" data-testid="onepager-edit">
                  <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="relative w-full overflow-hidden" style={{ backgroundColor: cover, minHeight: 320 }}>
        {data.hero_image_url && (
          <img src={data.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        )}
        <div className="relative max-w-4xl mx-auto px-6 py-16 text-white">
          {editing ? (
            <>
              <Input
                className="bg-white/20 border-white/30 text-white placeholder:text-white/60 text-2xl font-bold mb-3"
                value={data.display_name || ""}
                onChange={(e) => setData({ ...data, display_name: e.target.value })}
                placeholder="Your brand display name"
                data-testid="onepager-name-input"
              />
              <Input
                className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                value={data.tagline || ""}
                onChange={(e) => setData({ ...data, tagline: e.target.value })}
                placeholder="One line that sells your value"
                data-testid="onepager-tagline-input"
              />
              <label className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-sm rounded-md cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Replace hero image
                <input type="file" accept="image/*" onChange={uploadHero} className="hidden" data-testid="onepager-hero-upload" />
              </label>
            </>
          ) : (
            <>
              <h1 className="text-4xl sm:text-5xl font-bold mb-3">{data.display_name || "Your Brand"}</h1>
              <p className="text-xl text-white/90">{data.tagline || "Tap edit to add a tagline"}</p>
            </>
          )}
        </div>
      </div>

      {/* Intro + Value */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Why brands choose to work with me</h2>
          {editing ? (
            <Textarea
              rows={5}
              value={data.intro || ""}
              onChange={(e) => setData({ ...data, intro: e.target.value })}
              placeholder="A short intro that frames the value you deliver..."
              data-testid="onepager-intro-input"
            />
          ) : (
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.intro || "Your intro story goes here."}</p>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">The value I bring</h2>
          <ul className="space-y-2">
            {(data.value_bullets || []).map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: cover }} />
                {editing ? (
                  <Input
                    value={b}
                    onChange={(e) => {
                      const next = [...(data.value_bullets || [])];
                      next[i] = e.target.value;
                      setData({ ...data, value_bullets: next });
                    }}
                    data-testid={`onepager-bullet-${i}`}
                  />
                ) : (
                  <span>{b}</span>
                )}
              </li>
            ))}
            {editing && (
              <li>
                <Button size="sm" variant="ghost" onClick={() => setData({ ...data, value_bullets: [...(data.value_bullets || []), ""] })}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add value point
                </Button>
              </li>
            )}
          </ul>
        </section>

        {/* Products grid */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Products I use in my work</h2>
            {!isPublicView && (
              <Button size="sm" variant="outline" onClick={() => setShowAddProduct(true)} data-testid="onepager-add-product-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add product
              </Button>
            )}
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-gray-500">No products yet. Tap Add product to start building your brand portfolio.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {products.map((p) => (
                <Card key={p.submission_id || p.product_id} className="overflow-hidden" data-testid={`onepager-product-${p.submission_id || p.product_id}`}>
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ShoppingBag className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">{p.brand_name}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{p.product_name}</p>
                    {p.shade && <p className="text-xs text-gray-500 mt-0.5">{p.shade}</p>}
                    {p.status && p.status !== "linked" && p.status !== "promoted" && (
                      <Badge className="mt-1.5 text-[10px] bg-amber-50 text-amber-700 border-amber-200">{p.status.replace(/_/g, " ")}</Badge>
                    )}
                    {p.is_house_brand && (
                      <Badge className="mt-1.5 ml-1 text-[10px] bg-violet-50 text-violet-700 border-violet-200">My line</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Socials */}
        <section className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Stay connected</h2>
          <div className="flex flex-wrap gap-3">
            {data.social_links?.instagram && (
              <a href={`https://instagram.com/${data.social_links.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-gray-50">
                <Instagram className="w-4 h-4" /> @{data.social_links.instagram.replace(/^@/, "")}
              </a>
            )}
            {data.social_links?.website && (
              <a href={data.social_links.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-gray-50">
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
          </div>
        </section>
      </div>

      {showAddProduct && (
        <AddProductModal
          providerId={providerId}
          coverColor={cover}
          onClose={() => setShowAddProduct(false)}
          onSaved={(newProd) => setData({ ...data, products: [newProd, ...products] })}
        />
      )}
    </div>
  );
}

function AddProductModal({ providerId, onClose, onSaved, coverColor }) {
  const [form, setForm] = useState({
    brand_name: "", product_name: "", shade: "", photo_url: "", buy_url: "",
    is_house_brand: false, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("folder", "host-products");
    const res = await fetch(`${API}/api/pilot/upload-image`, { method: "POST", body: fd });
    if (res.ok) {
      const j = await res.json();
      setForm({ ...form, photo_url: j.url });
    } else toast.error("Upload failed");
    setUploading(false);
  };

  const submit = async () => {
    if (!form.brand_name.trim() || !form.product_name.trim()) {
      toast.error("Brand + product name required");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/host/products/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, provider_id: providerId }),
      });
      if (res.ok) {
        const data = await res.json();
        onSaved(data);
        toast.success("Product submitted. Admin will link it to a brand.");
        onClose();
      } else toast.error("Submit failed");
    } catch (_) { toast.error("Submit failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="add-product-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Add a product</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Brand</label>
            <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="MAC, NARS, ShelseaLuxe..." data-testid="add-product-brand" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Product name</label>
            <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Studio Fix Powder" data-testid="add-product-name" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Shade / variant <span className="text-gray-400">(optional)</span></label>
            <Input value={form.shade} onChange={(e) => setForm({ ...form, shade: e.target.value })} placeholder="NC42" data-testid="add-product-shade" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Photo</label>
            {form.photo_url ? (
              <div className="relative">
                <img src={form.photo_url} alt="" className="w-24 h-24 rounded-lg object-cover" />
                <button onClick={() => setForm({ ...form, photo_url: "" })} className="absolute -top-2 -right-2 bg-white border rounded-full p-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed rounded-md cursor-pointer text-sm hover:bg-gray-50">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload photo"}
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="add-product-photo" />
              </label>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Buy URL <span className="text-gray-400">(optional)</span></label>
            <Input value={form.buy_url} onChange={(e) => setForm({ ...form, buy_url: e.target.value })} placeholder="https://..." />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_house_brand} onChange={(e) => setForm({ ...form, is_house_brand: e.target.checked })} data-testid="add-product-house" />
            <span>This is from my own line (e.g. ShelseaLuxe)</span>
          </label>
          <Button onClick={submit} disabled={saving} className="w-full" style={{ backgroundColor: coverColor }} data-testid="add-product-submit">
            {saving ? "Submitting..." : "Submit for admin review"}
          </Button>
        </div>
      </div>
    </div>
  );
}
